/**
 * Seção de módulos ativos na mesa (visível para todos)
 */

import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useModules } from '@/hooks/useModules'
import { PuzzlePiece } from '@phosphor-icons/react'

interface TableModulesSectionProps {
  tableId: string
  isGameMaster: boolean
  refreshKey?: number
}

export function TableModulesSection({ tableId, isGameMaster, refreshKey }: TableModulesSectionProps) {
  const navigate = useNavigate()
  const { tableModules, isLoading, listTableModules } = useModules()

  useEffect(() => {
    if (tableId) {
      listTableModules(tableId).catch((err) =>
        console.error('Erro ao carregar módulos:', err)
      )
    }
  }, [tableId, refreshKey])

  const activeModules = tableModules.filter(m => m.is_active)

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PuzzlePiece size={20} />
            Módulos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="text-gray-500">Carregando módulos...</div>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (activeModules.length === 0) {
    return null // Não mostrar seção se não houver módulos ativos
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <PuzzlePiece size={20} />
              Módulos Ativos
            </CardTitle>
            <CardDescription>
              Funcionalidades extras ativadas nesta mesa
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {activeModules.map((tableModule) => {
            // Módulo Book IA
            if (tableModule.module_id === 'book_ia') {
              return (
                <div key={tableModule.id} className="border rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-lg font-semibold flex items-center gap-2">
                        Book IA
                        <Badge className="text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full border-none bg-status-success/20 text-status-success">
                          Ativo
                        </Badge>
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Escrita interativa de livro com narrador IA, solo ou colaborativo
                      </p>
                    </div>
                  </div>

                  {isGameMaster ? (
                    <div>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/modules/book_ia/config/${tableId}`)}
                        >
                          Configurar Book IA
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/modules/book_ia/monitor/${tableId}`)}
                        >
                          Acompanhar livros
                        </Button>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Configure a chave de API e carregue a aventura para liberar o livro aos jogadores.
                      </p>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">
                      Use o botão "Entrar no livro interativo" na parte superior da mesa para acessar sua sessão.
                    </p>
                  )}
                </div>
              )
            }

            // Outros módulos genéricos
            const moduleNames: Record<string, string> = {
              'dice': 'Sistema de Dados',
              'chat': 'Chat',
              'poll': 'Enquetes',
              'transmission': 'Transmissão',
            }
            const moduleDescriptions: Record<string, string> = {
              'dice': 'Sistema de rolagem de dados para suas aventuras',
              'chat': 'Comunicação em tempo real entre jogadores',
              'poll': 'Crie enquetes e votações para a mesa',
              'transmission': 'Transmita conteúdo para os jogadores',
            }
            return (
              <div key={tableModule.id} className="border rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      {moduleNames[tableModule.module_id] || tableModule.module_id}
                      <Badge className="text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full border-none bg-status-success/20 text-status-success">
                        Ativo
                      </Badge>
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {moduleDescriptions[tableModule.module_id] || 'Módulo adicional para sua campanha'}
                    </p>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}
